import { Palette } from "@bluframe/blublocks"
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  TechSpecCategory,
  TechSpecIdentifier
} from "@bluframe/mightychroma-mightytool-frontend-types"
import useDeepCompareMemoize from "helpers/useDeepCompareMemoize"
import { memo, useMemo } from "react"

import List from "./List"

export interface Props {
  onAdd?: (techSpec: TechSpecIdentifier[]) => () => void

  onRemove?: (techSpec: TechSpecIdentifier[]) => () => void
  techSpecs: TechSpecIdentifier[][]
}

export interface ComponentProps extends Omit<Props, "techSpecs"> {
  techSpecsAndColor: {
    color: keyof Palette
    techSpec: TechSpecIdentifier[]
  }[]
}

const CATEGORIES_COLORS_MAP: Record<TechSpecCategory, keyof Palette> = {
  Audio: "primary",
  Digital: "error",
  Discs: "success",
  Packaging: "warning",
  Subtitles: "alert",
  Video: "secondary"
}

export const usePrepareComponent = ({
  techSpecs,
  ...props
}: Props): ComponentProps => {
  // Deep-memoize `techSpecs` (array-of-arrays) so `techSpecsAndColor` stays stable when values don't change.
  const stableTechSpecs = useDeepCompareMemoize(techSpecs)

  const techSpecsAndColor = useMemo(
    () =>
      stableTechSpecs.map((techSpec) => {
        let color: keyof Palette = "success"

        for (const category in CATEGORIES_COLORS_MAP) {
          if (techSpec[0].includes(category as TechSpecCategory)) {
            color = CATEGORIES_COLORS_MAP[category as TechSpecCategory]
            break
          }
        }

        return {
          color,
          techSpec
        }
      }),
    [stableTechSpecs]
  )

  return {
    ...props,
    techSpecsAndColor
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  memo(List)
)
